import { useCallback, useEffect, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { jornadasDelEquipo, kpiDinamicaDelDia, pdvsConDinamica } from '../api/canjes';
import { resumirEquipo, totalesEquipo, type FilaEquipo } from '../domain/desempeno';

const TZ = 'America/Lima';

/** Primer y último día del mes YYYY-MM, sin `Date` para el último (evita zonas horarias). */
function rangoDelMes(mes: string): { desde: string; hasta: string } {
  const [a, m] = mes.split('-').map(Number);
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  return { desde: `${mes}-01`, hasta: `${mes}-${String(ultimo).padStart(2, '0')}` };
}

/**
 * Resumen del equipo de una campaña en un mes (U9).
 *
 * Pedido del usuario: *"que el supervisor vea el resumen de todos los recursos de su campaña"*.
 * La vista individual ya existía, pero comparar veinte personas exigía abrir veinte pantallas y
 * anotar a mano.
 *
 * LO QUE HACE ÚTIL A ESTA PANTALLA ES EL ORDEN, no la tabla: arriba va quien tiene más días con
 * dinámica **en cero**, después quien tiene peor promedio. Existe para encontrar a quién hay que
 * ayudar; ordenada alfabéticamente habría que leerla entera para saber lo mismo.
 *
 * Y EL DENOMINADOR, que se explica a la vista: el promedio cuenta solo las jornadas donde corría
 * una dinámica y la persona asistió. Dividir entre todas castigaría a quien pasó el mes en tiendas
 * sin promoción, donde no hay nada que registrar.
 */
export default function ResumenEquipo({ campaignId }: { campaignId: string }) {
  const [mes, setMes] = useState(() => formatInTimeZone(new Date(), TZ, 'yyyy-MM'));
  const [filas, setFilas] = useState<FilaEquipo[]>([]);
  const [etiqueta, setEtiqueta] = useState('Dinámica');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { desde, hasta } = rangoDelMes(mes);
      const jornadas = await jornadasDelEquipo(campaignId, desde, hasta);

      // La vigencia se pregunta por DÍA, igual que en la vista individual: una tienda puede tener
      // dinámica un sábado y no el martes, y de eso depende si un 0 cuenta o no aplica.
      const dias = [...new Set(jornadas.map((j) => j.fecha))];
      const vigencias = new Map<string, Set<string>>();
      await Promise.all(dias.map(async (d) => { vigencias.set(d, await pdvsConDinamica(d)); }));

      setFilas(resumirEquipo(jornadas.map((j) => ({
        workerId: j.workerId,
        nombre: j.workerNombre,
        dni: j.workerDni,
        fecha: j.fecha,
        pdvId: j.pdvId,
        asistio: j.asistio,
        canjes: j.canjes,
        conDinamica: j.pdvId ? (vigencias.get(j.fecha)?.has(j.pdvId) ?? false) : false,
      }))));

      // El nombre del indicador sale de la campaña (0035), no se cablea "Canjes": rotular así lo
      // que son raspaditas convierte el indicador en algo que nadie sabe leer.
      if (dias.length > 0) {
        const kpis = await kpiDinamicaDelDia(dias.sort()[dias.length - 1]);
        const nombres = new Set([...kpis.values()].map((k) => k.label));
        setEtiqueta(nombres.size === 1 ? [...nombres][0] : 'Dinámica');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el resumen del equipo.');
    } finally {
      setCargando(false);
    }
  }, [campaignId, mes]);

  useEffect(() => { void cargar(); }, [cargar]);

  const t = totalesEquipo(filas);

  return (
    <section className="rounded border bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Resumen del equipo</h2>
        <label className="text-sm">
          Mes{' '}
          <input
            type="month"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            className="rounded border px-2 py-1"
          />
        </label>
      </div>

      {error && <p className="mb-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      {cargando && <p className="text-sm text-gray-500">Cargando…</p>}

      {!cargando && filas.length === 0 && !error && (
        <p className="text-sm text-gray-500">Nadie tuvo jornadas aprobadas en este mes.</p>
      )}

      {!cargando && filas.length > 0 && (
        <>
          <p className="mb-3 text-sm text-gray-700">
            <strong>{t.personas}</strong> persona(s) · <strong>{t.asistidas}</strong> de{' '}
            <strong>{t.jornadas}</strong> jornadas asistidas · <strong>{t.canjes}</strong>{' '}
            {etiqueta.toLowerCase()} en total
            {t.promedio !== null && <> · promedio del equipo <strong>{t.promedio}</strong></>}
            {t.enCero > 0 && (
              <> · <strong className="text-amber-700">{t.enCero}</strong> día(s) con dinámica sin registrar</>
            )}
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border text-sm">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="p-2">Trabajador</th>
                  <th className="p-2 text-center">Asistidas</th>
                  <th className="p-2 text-center">Con dinámica</th>
                  <th className="p-2 text-center">{etiqueta}</th>
                  <th className="p-2 text-center">Promedio</th>
                  <th className="p-2 text-center">En cero</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f.workerId} className="border-t">
                    <td className="p-2">
                      {f.nombre}
                      <span className="ml-1 text-xs text-gray-500">{f.dni}</span>
                    </td>
                    <td className="p-2 text-center">{f.asistidas}/{f.jornadas}</td>
                    <td className="p-2 text-center">{f.jornadasConDinamica}</td>
                    <td className="p-2 text-center">{f.canjes}</td>
                    <td className="p-2 text-center">
                      {/* El guion NO es un cero: es "no tuvo ninguna jornada con dinámica", o sea
                          que no hubo nada que registrar. Fundirlos acusaría a alguien de algo que
                          no pasó. */}
                      {f.promedio === null
                        ? <span className="text-gray-400" title="No tuvo jornadas con dinámica">—</span>
                        : f.promedio}
                    </td>
                    <td className="p-2 text-center">
                      {f.enCero > 0
                        ? <span className="font-semibold text-amber-700">{f.enCero}</span>
                        : <span className="text-gray-400">0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-2 text-xs text-gray-500">
            Ordenado por lo que hay que mirar: primero quien tiene más días con dinámica sin
            registrar, después el promedio más bajo. El <strong>promedio</strong> cuenta solo las
            jornadas donde corría una dinámica y la persona asistió — dividir entre todas
            castigaría a quien trabajó en tiendas sin promoción, donde no hay nada que registrar.
            Un guion significa que no tuvo ninguna de esas jornadas.
          </p>
        </>
      )}
    </section>
  );
}
